import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, X, Loader2, Quote } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const empty = { name: "", role: "", content: "", published: false };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (t) => { setForm({ name: t.name, role: t.role || "", content: t.content, published: t.published }); setEditing(t.id); };

  const save = async () => {
    if (!form.name.trim() || !form.content.trim()) { toast.error("Name and content are required."); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/testimonials", form);
      else await api.put(`/admin/testimonials/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const togglePublish = async (t) => {
    try { await api.put(`/admin/testimonials/${t.id}`, { ...t, published: !t.published }); load(); }
    catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const inputCls = "w-full rounded-xl border border-soft-border bg-white p-3 text-sm outline-none focus:border-terracotta";

  return (
    <div data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Testimonials</h1>
          <p className="mt-1 text-sm text-taupe">Add genuine patient testimonials. Only published ones appear on the website.</p>
        </div>
        <button data-testid="testimonial-add-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-3 text-sm font-semibold text-cream hover:bg-terracotta-dark"><Plus className="h-4 w-4" /> Add Testimonial</button>
      </div>

      {loading ? <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div> : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.length === 0 && <p className="text-sm text-taupe">No testimonials yet.</p>}
          {items.map((t) => (
            <div key={t.id} data-testid="testimonial-card" className="rounded-2xl border border-soft-border bg-cream p-6">
              <div className="flex items-center justify-between">
                <Quote className="h-6 w-6 text-gold/60" />
                <span className={`text-xs font-semibold ${t.published ? "text-green-600" : "text-taupe"}`}>{t.published ? "Published" : "Draft"}</span>
              </div>
              <p className="mt-3 line-clamp-4 text-sm text-taupe">"{t.content}"</p>
              <p className="mt-3 font-serif text-charcoal">{t.name}{t.role ? <span className="ml-1 text-xs text-taupe">· {t.role}</span> : null}</p>
              <div className="mt-4 flex items-center gap-1">
                <button onClick={() => openEdit(t)} className="rounded-lg p-2 text-terracotta hover:bg-blush" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => togglePublish(t)} className="rounded-lg p-2 text-taupe hover:bg-blush" aria-label="Toggle publish">{t.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                <button onClick={() => remove(t.id)} className="rounded-lg p-2 text-destructive hover:bg-rose-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/40 p-4" onClick={() => setEditing(null)}>
          <div data-testid="testimonial-modal" className="w-full max-w-lg rounded-3xl bg-background p-6 shadow-lift md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl text-charcoal">{editing === "new" ? "Add Testimonial" : "Edit Testimonial"}</h2>
              <button onClick={() => setEditing(null)} className="rounded-lg p-2 text-taupe hover:bg-blush"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-charcoal">Patient Name</label>
                <input data-testid="testimonial-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-charcoal">Role / Label (optional)</label>
                <input data-testid="testimonial-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} placeholder="e.g. Maternity patient" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-charcoal">Content</label>
                <textarea data-testid="testimonial-content" rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={inputCls} />
              </div>
              <label className="flex items-center gap-3">
                <input type="checkbox" data-testid="testimonial-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-5 w-5 accent-terracotta" />
                <span className="text-sm text-charcoal">Publish on website</span>
              </label>
            </div>
            <button data-testid="testimonial-save" onClick={save} disabled={saving} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-sm font-semibold text-cream hover:bg-terracotta-dark disabled:opacity-70">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Testimonial
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
